"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { useParams, useRouter } from "next/navigation"
import { MapPin, Briefcase, Clock, Calendar, DollarSign, ArrowLeft, Edit, Trash2, Loader2 } from "lucide-react"
import JobApplicationForm from "@/components/JobApplicationForm"
import JobApplicationsList from "@/components/JobApplicationsList"
import Header from "@/components/Header"
import { formatDistanceToNow } from "date-fns"

export default function JobDetailView() {
  const params = useParams()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [jobData, setJobData] = useState<any>(null)

  useEffect(() => {
    const fetchJobData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await fetch(`/api/jobs/${params.id}`)

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Job not found")
          }
          throw new Error("Failed to load job details")
        }

        const data = await response.json()
        setJobData(data)
      } catch (err) {
        console.error("Error fetching job:", err)
        setError(err instanceof Error ? err.message : "An error occurred")
      } finally {
        setIsLoading(false)
      }
    }

    if (params.id) {
      fetchJobData()
    }
  }, [params.id])

  const handleDeleteJob = async () => {
    if (!confirm("Are you sure you want to delete this job posting?")) {
      return
    }

    try {
      const response = await fetch(`/api/jobs/${params.id}`, {
        method: "DELETE",
      })

      if (!response.ok) {
        throw new Error("Failed to delete job")
      }

      router.push("/jobs")
    } catch (err) {
      console.error("Error deleting job:", err)
      alert("Failed to delete job. Please try again.")
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 text-blue-600 animate-spin" />
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
          <p className="text-gray-600 mb-6">
            {error === "Job not found"
              ? "The job you're looking for doesn't exist or has been removed."
              : "We're having trouble loading this job. This might be due to a temporary issue."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Try again
            </button>
            <Link
              href="/jobs"
              className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to jobs
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // If job data is not available
  if (!jobData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Job Not Found</h1>
          <p className="mb-6 text-gray-600">The job you're looking for doesn't exist or has been removed.</p>
          <Link href="/jobs" className="text-blue-600 hover:text-blue-800">
            Return to Jobs
          </Link>
        </div>
      </div>
    )
  }

  const job = jobData
  const isAuthenticated = !!job.currentUser
  const isJobPoster = job.postedBy && job.currentUser && job.postedBy._id === job.currentUser.id
  const isTradesperson = job.currentUser && job.currentUser.userType?.toLowerCase() === "tradesperson"
  const hasApplied = job.applicants?.some((applicant: any) => applicant.user?.toString() === job.currentUser?.id)
  const postedDate = job.createdAt ? formatDistanceToNow(new Date(job.createdAt), { addSuffix: true }) : "recently"

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      {isAuthenticated ? (
        <Header
          userId={job.currentUser.id}
          userName={job.currentUser.name || "User"}
          notifications={{
            unreadMessageCount: 0,
            pendingConnectionCount: 0,
          }}
        />
      ) : (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <Link href="/" className="text-xl font-bold text-blue-600">
                    Blue Collar Social
                  </Link>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  href="/jobs"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Jobs
                </Link>
                <Link
                  href="/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Login
                </Link>
                <Link
                  href="/register"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Register
                </Link>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="mb-6">
            <Link href="/jobs" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Jobs
            </Link>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-col sm:flex-row justify-between items-start">
                <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
                {isJobPoster && (
                  <div className="flex space-x-2 mt-4 sm:mt-0">
                    <Link
                      href={`/jobs/${params.id}/edit`}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Link>
                    <button
                      onClick={handleDeleteJob}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500 gap-3">
                {job.location && (
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {job.location}
                  </div>
                )}
                {job.trade && (
                  <div className="flex items-center">
                    <Briefcase className="h-4 w-4 mr-1" />
                    <span className="capitalize">{job.trade}</span>
                  </div>
                )}
                {job.timeline && (
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span className="capitalize">
                      {job.timeline === "urgent"
                        ? "Urgent"
                        : job.timeline === "week"
                          ? "Within a week"
                          : job.timeline === "month"
                            ? "Within a month"
                            : job.timeline === "future"
                              ? "Future project"
                              : "Flexible"}
                    </span>
                  </div>
                )}
                {job.budget && (
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    {job.budget}
                  </div>
                )}
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Posted {postedDate}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium text-gray-900">Job Description</h3>
              <div className="mt-3 text-sm text-gray-600 whitespace-pre-wrap">{job.description}</div>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium text-gray-900">Posted By</h3>
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-bold">
                      {job.postedBy?.name ? job.postedBy.name.charAt(0).toUpperCase() : "?"}
                    </div>
                  </div>
                  <div className="ml-3">
                    <Link
                      href={`/profile/${job.postedBy?._id}`}
                      className="text-sm font-medium text-blue-600 hover:underline"
                    >
                      {job.postedBy?.name}
                    </Link>
                    <p className="text-sm text-gray-500 capitalize">{job.postedBy?.userType}</p>
                    <p className="text-sm text-gray-500">{job.postedBy?.location}</p>
                  </div>
                </div>
                {isAuthenticated && !isJobPoster && (
                  <div className="mt-4 sm:mt-0 sm:ml-auto">
                    <Link
                      href={`/messages/new?recipient=${job.postedBy?._id}`}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Contact
                    </Link>
                  </div>
                )}
                {!isAuthenticated && (
                  <div className="mt-4 sm:mt-0 sm:ml-auto">
                    <Link
                      href={`/login?callbackUrl=/jobs/${params.id}`}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Login to Contact
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Job application section for tradespeople */}
            {isTradesperson && !isJobPoster && job.status === "open" && (
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <h3 className="text-lg font-medium text-gray-900">Apply for this Job</h3>
                {hasApplied ? (
                  <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-800">You have already applied for this job.</p>
                  </div>
                ) : (
                  <JobApplicationForm jobId={params.id as string} />
                )}
              </div>
            )}

            {/* Login to apply section for non-authenticated users */}
            {!isAuthenticated && job.status === "open" && (
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <h3 className="text-lg font-medium text-gray-900">Apply for this Job</h3>
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-blue-800">
                    Please{" "}
                    <Link href={`/login?callbackUrl=/jobs/${params.id}`} className="font-medium underline">
                      login
                    </Link>{" "}
                    or{" "}
                    <Link href="/register/tradesperson" className="font-medium underline">
                      register as a tradesperson
                    </Link>{" "}
                    to apply for this job.
                  </p>
                </div>
              </div>
            )}

            {/* Applications list for job posters */}
            {isJobPoster && (
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <h3 className="text-lg font-medium text-gray-900">Applications</h3>
                <JobApplicationsList jobId={params.id as string} applications={job.applicants || []} />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

